<!-- src/components/ChatPanel.svelte -->
<script>
  import { onMount } from 'svelte'
  import { API_BASE } from '$lib/api'

  let messages = []
  let input = ''
  let loading = false
  let chatContainer

  onMount(() => {
    messages = [
      { role: 'assistant', content: 'Hello! I\'m GREA, your AI assistant. How can I help you today?', timestamp: new Date().toISOString() }
    ]
  })

  async function sendMessage() {
    if (!input.trim() || loading) return

    const userMessage = { role: 'user', content: input.trim(), timestamp: new Date().toISOString() }
    messages = [...messages, userMessage]
    input = ''
    loading = true

    try {
      const res = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: messages.map(m => ({ role: m.role, content: m.content })),
        }),
      })

      const data = await res.json()

      if (data.content) {
        messages = [...messages, {
          role: 'assistant',
          content: data.content,
          model: data.model,
          provider: data.provider,
          timestamp: new Date().toISOString(),
        }]
      } else if (data.error) {
        messages = [...messages, {
          role: 'assistant',
          content: `Error: ${data.error}`,
          isError: true,
          timestamp: new Date().toISOString(),
        }]
      }
    } catch (err) {
      messages = [...messages, {
        role: 'assistant',
        content: `Connection error: ${err.message}. Make sure the Worker is deployed.`,
        isError: true,
        timestamp: new Date().toISOString(),
      }]
    } finally {
      loading = false
    }
  }

  function handleKeydown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  function formatTime(ts) {
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }
</script>

<div class="chat-panel glass-panel">
  <div class="chat-header">
    <div class="chat-title">
      <span class="chat-icon">⚡</span>
      <span>GREA Chat</span>
    </div>
    <div class="chat-status">
      <span class="status-dot active"></span>
      <span>Online</span>
    </div>
  </div>

  <div class="chat-messages" bind:this={chatContainer}>
    {#each messages as msg, i (msg.timestamp + i)}
      <div class="message" class:user={msg.role === 'user'} class:assistant={msg.role === 'assistant'} class:error={msg.isError}>
        <div class="message-avatar">
          {#if msg.role === 'user'}
            <span class="avatar-icon">👤</span>
          {:else}
            <span class="avatar-icon">⚡</span>
          {/if}
        </div>
        <div class="message-content">
          <div class="message-text">{msg.content}</div>
          <div class="message-meta">
            <span class="message-time">{formatTime(msg.timestamp)}</span>
            {#if msg.model}
              <span class="message-model">{msg.model}</span>
            {/if}
          </div>
        </div>
      </div>
    {/each}

    {#if loading}
      <div class="message assistant">
        <div class="message-avatar">
          <span class="avatar-icon">⚡</span>
        </div>
        <div class="message-content">
          <div class="typing-indicator">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </div>
    {/if}
  </div>

  <div class="chat-input-area">
    <div class="input-wrapper">
      <textarea
        bind:value={input}
        on:keydown={handleKeydown}
        placeholder="Type a message... (Enter to send, Shift+Enter for newline)"
        rows="1"
        disabled={loading}
      ></textarea>
      <button class="send-btn" on:click={sendMessage} disabled={loading || !input.trim()}>
        <span>↑</span>
      </button>
    </div>
  </div>
</div>

<style>
  .chat-panel {
    display: flex;
    flex-direction: column;
    height: 100%;
    max-height: 600px;
    padding: 0;
    overflow: hidden;
  }

  .chat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--glass-border);
  }

  .chat-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    font-size: 15px;
  }

  .chat-icon {
    font-size: 18px;
  }

  .chat-status {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: var(--text-secondary);
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .message {
    display: flex;
    gap: 12px;
    max-width: 85%;
    animation: messageIn 0.2s ease-out;
  }

  @keyframes messageIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .message.user {
    align-self: flex-end;
    flex-direction: row-reverse;
  }

  .message.assistant {
    align-self: flex-start;
  }

  .message.error .message-text {
    color: #ef4444;
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.2);
  }

  .message-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    flex-shrink: 0;
  }

  .avatar-icon {
    font-size: 14px;
  }

  .message-content {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .message-text {
    padding: 12px 16px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-md);
    font-size: 14px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .message.user .message-text {
    background: rgba(124, 92, 255, 0.15);
    border-color: rgba(124, 92, 255, 0.3);
  }

  .message-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: var(--text-muted);
    padding: 0 4px;
  }

  .message-model {
    background: var(--glass-bg);
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
  }

  .typing-indicator {
    display: flex;
    gap: 4px;
    padding: 12px 16px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-md);
  }

  .typing-indicator span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--text-muted);
    animation: typing 1.4s infinite;
  }

  .typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
  .typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

  @keyframes typing {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.4; }
    30% { transform: translateY(-4px); opacity: 1; }
  }

  .chat-input-area {
    padding: 16px 20px;
    border-top: 1px solid var(--glass-border);
  }

  .input-wrapper {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    background: var(--glass-bg);
    border: 1px solid var(--glass-border);
    border-radius: var(--radius-lg);
    padding: 8px 12px;
    transition: all var(--transition-fast);
  }

  .input-wrapper:focus-within {
    border-color: var(--accent-primary);
    box-shadow: 0 0 0 3px rgba(124, 92, 255, 0.1);
  }

  textarea {
    flex: 1;
    border: none;
    background: transparent;
    color: var(--text-primary);
    font-size: 14px;
    line-height: 1.4;
    resize: none;
    outline: none;
    max-height: 120px;
    min-height: 20px;
    font-family: inherit;
  }

  textarea::placeholder {
    color: var(--text-muted);
  }

  textarea:disabled {
    opacity: 0.5;
  }

  .send-btn {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: var(--accent-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all var(--transition-fast);
    flex-shrink: 0;
  }

  .send-btn:hover:not(:disabled) {
    background: #6a4ce0;
    transform: scale(1.05);
  }

  .send-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .send-btn span {
    font-size: 16px;
    line-height: 1;
  }
</style>
